// @ts-strict-ignore
import Dropdown from 'antd/lib/dropdown';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Modal from 'antd/lib/modal';
import Input from 'antd/lib/input';
import sparkleIcon from '../../../assets/sparkle.svg';
import React from 'react';
import { CopyOutlined, DownOutlined } from '@ant-design/icons';
import { useGetGeneratedReleaseNotes } from '@/components/useGetGeneratedReleaseNotes';
import { globalMessage } from '@/utils/globalMessage';
import { ReleaseComponentProps } from '@/typescriptHelpers';
import {
    GeneratedReleaseNotesMarkdownContext,
    GeneratedReleaseNotesStepContext,
    GeneratedReleaseNotesType,
} from '@/types';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectAIDisabledForReleaseNotes,
    selectCurrentSubscriptionStatus,
    selectOrgSettings,
    selectUserIsRunwayAdmin,
} from '@/selectors/selectors';
import useUserBelongsToApp from '@/utils/hooks/useUserBelongsToApp';
import { MenuProps } from 'antd/lib/menu';
import { isSubscriptionPlanPaidCustomer } from '@/helpers';
import Text from 'antd/lib/typography/Text';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';

const releaseNotesAdditionalContextMaxLength = 2048;

function isAIGeneratedReleaseNotesType(notesType: GeneratedReleaseNotesType): boolean {
    return (
        notesType === GeneratedReleaseNotesType.ReleaseNotesTypeAIGeneratedExternal ||
        notesType === GeneratedReleaseNotesType.ReleaseNotesTypeAIGeneratedInternal
    );
}

interface GenerateReleaseNotesDropdownProps extends ReleaseComponentProps {
    locale?: string;
    markdownContext: GeneratedReleaseNotesMarkdownContext;
    stepContext: GeneratedReleaseNotesStepContext;
    disallowedTypes?: GeneratedReleaseNotesType[];
}

export const GenerateReleaseNotesDropdown: React.FC<GenerateReleaseNotesDropdownProps> = ({
    orgId,
    appId,
    releaseId,
    stepContext,
    markdownContext,
    locale,
    disallowedTypes,
}) => {
    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));
    const userBelongsToApp = useUserBelongsToApp(appId);
    const isACLEnabled = useSelector((state: RootState) => selectOrgSettings(state).isACLEnabled);

    const currentSubscriptionStatus = useSelector((state: RootState) => selectCurrentSubscriptionStatus(state));
    const isPaidCustomer = isSubscriptionPlanPaidCustomer(currentSubscriptionStatus); // we'll only allow paid customers to append additional context to AI prompts, to mitigate potential abuse

    const shouldAllowAIGeneratedReleaseNotes = useSelector(
        (state: RootState) => !selectAIDisabledForReleaseNotes(state, orgId),
    );

    const [generatedNotes, setGeneratedNotes] = React.useState('');
    const [isCopyButtonVisible, setIsCopyButtonVisible] = React.useState(false);
    const [isCopyButtonDisabled, setIsCopyButtonDisabled] = React.useState(true);
    const [pendingAIRequest, setPendingAIRequest] = React.useState<GeneratedReleaseNotesType | null>(null);
    const [additionalContextInput, setAdditionalContextInput] = React.useState('');

    const [getGeneratedReleaseNotes, isLoadingGetGeneratedReleaseNotes, getGeneratedReleaseNotesError] =
        useGetGeneratedReleaseNotes(orgId, appId, releaseId);

    const resolvedLocale = locale && locale !== '' ? locale : 'en-US';

    const requestGeneratedNotes = async (
        notesType: GeneratedReleaseNotesType,
        additionalUserContext?: string,
    ): Promise<boolean> => {
        setGeneratedNotes('');
        setIsCopyButtonDisabled(true);

        const notes = await getGeneratedReleaseNotes(
            notesType,
            stepContext,
            markdownContext,
            resolvedLocale,
            additionalUserContext,
        );

        if (notes !== null && notes !== undefined) {
            globalMessage.success('Release notes were generated successfully and are ready to be copied to clipboard.');
            setGeneratedNotes(notes);
            setIsCopyButtonVisible(true);
            setIsCopyButtonDisabled(false);
            return true;
        }
        return false;
    };

    const handleClipboardWrite = async () => {
        navigator.clipboard
            .writeText(generatedNotes)
            .then(() => {
                globalMessage.success('Generated release notes copied to clipboard.');
            })
            .catch((e) => {
                globalMessage.error(
                    `Error copying generated release notes to clipboard: ${e}; please check your browser's clipboard permissions.`,
                );
            })
            .finally(() => {
                setGeneratedNotes('');
                setIsCopyButtonDisabled(true);
            });
    };

    React.useEffect(() => {
        if (getGeneratedReleaseNotesError) {
            globalMessage.error(`Failed to generate release notes: ${getGeneratedReleaseNotesError}`);
        }
    }, [getGeneratedReleaseNotesError]);

    const allNotesTypeItems: MenuProps['items'] = [
        {
            key: GeneratedReleaseNotesType.ReleaseNotesTypeAIGeneratedExternal,
            label: 'Generate with AI (for external audience)',
        },
        {
            key: GeneratedReleaseNotesType.ReleaseNotesTypeAIGeneratedInternal,
            label: 'Generate with AI (for internal audience)',
        },
        {
            key: GeneratedReleaseNotesType.ReleaseNotesTypeTicketFocus,
            label: 'Work items diff (ticket focus)',
        },
        {
            key: GeneratedReleaseNotesType.ReleaseNotesTypeCodeFocus,
            label: 'Work items diff (code focus)',
        },
        {
            key: GeneratedReleaseNotesType.ReleaseNotesTypeTemplated,
            label: 'Templated release summary',
        },
    ];
    const allowedNotesTypeItems = allNotesTypeItems.filter((item) => {
        const noteType = item.key as GeneratedReleaseNotesType;

        if (
            ((noteType == GeneratedReleaseNotesType.ReleaseNotesTypeAIGeneratedExternal ||
                noteType == GeneratedReleaseNotesType.ReleaseNotesTypeAIGeneratedInternal) &&
                !shouldAllowAIGeneratedReleaseNotes) ||
            (disallowedTypes && disallowedTypes.includes(noteType))
        ) {
            return false;
        } else {
            return true;
        }
    });

    const handleMenuClick = (e) => {
        const notesType = e.key as GeneratedReleaseNotesType;
        if (isAIGeneratedReleaseNotesType(notesType) && isPaidCustomer) {
            setAdditionalContextInput('');
            setPendingAIRequest(notesType);
            return;
        }
        requestGeneratedNotes(notesType);
    };

    const submitPendingAIRequest = async (aiRequestType: GeneratedReleaseNotesType) => {
        if (!aiRequestType) {
            return;
        }

        const trimmed = additionalContextInput.trim();
        setPendingAIRequest(null);
        const succeeded = await requestGeneratedNotes(aiRequestType, trimmed !== '' ? trimmed : undefined);
        if (succeeded) {
            setAdditionalContextInput('');
        }
    };

    const cancelPendingAIRequest = () => {
        setPendingAIRequest(null);
        setAdditionalContextInput('');
    };

    const menuWithProps = {
        items: allowedNotesTypeItems,
        onClick: handleMenuClick,
    };

    const pendingAIRequestTitle =
        pendingAIRequest && pendingAIRequest == GeneratedReleaseNotesType.ReleaseNotesTypeAIGeneratedExternal ?
            'Generate notes (for external audience)'
        : pendingAIRequest && pendingAIRequest == GeneratedReleaseNotesType.ReleaseNotesTypeAIGeneratedInternal ?
            'Generate notes (for internal audience)'
        :   'Generate notes';

    return (
        <Space size="small" wrap>
            <Dropdown
                menu={menuWithProps}
                trigger={['click']}
                disabled={isACLEnabled && !userBelongsToApp && !isRunwayAdmin}
            >
                <Button size="small" loading={isLoadingGetGeneratedReleaseNotes}>
                    <Space>
                        <img src={sparkleIcon} width={15} height={15} /> Auto-generate release notes <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
            {isCopyButtonVisible ?
                <Button size="small" disabled={isCopyButtonDisabled} type="link" onClick={handleClipboardWrite}>
                    <CopyOutlined /> Copy generated notes
                </Button>
            :   null}
            <Modal
                title={pendingAIRequestTitle}
                open={pendingAIRequest !== null}
                onCancel={cancelPendingAIRequest}
                onOk={() => submitPendingAIRequest(pendingAIRequest)}
                okText="Generate"
                confirmLoading={isLoadingGetGeneratedReleaseNotes}
                style={{ paddingTop: 20 }}
            >
                <Text type="secondary" style={{ marginBottom: 8, display: 'inline-block' }}>
                    {`Optional: add instructions or context for the model. These are appended to Runway's standard prompt for generating notes.`}
                    <TooltipInfoIcon
                        tooltipContent="Runway's default prompt looks at project management tickets and code changes (PRs and commits — no actual source code) to generate notes summarizing a given app release. The prompt also factors in constraints like character limits and markdown compatibility enforced by the store in question, and tailors style to your selected audience (external or internal)."
                        style={{ marginLeft: 4 }}
                    />
                </Text>
                <Input.TextArea
                    value={additionalContextInput}
                    onChange={(ev) => setAdditionalContextInput(ev.target.value)}
                    rows={6}
                    maxLength={releaseNotesAdditionalContextMaxLength}
                    showCount
                    autoFocus
                    placeholder="E.g. emphasize security fixes, use a more formal tone, or highlight specific features."
                />
            </Modal>
        </Space>
    );
};
